import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { BudgetRevisionSettings, RevisionReasonCategoryConfig } from '@/types/resource-budget-settings.types';
import { DEFAULT_REVISION_REASON_CATEGORIES } from '@/types/resource-budget-settings.types';
import { fetchRevisionReasonCategories } from '@/lib/data-service';
import {
  getRevisionSettings,
  fetchRevisionSettings,
  saveRevisionSettings as persistRevisionSettings,
  DEFAULT_REVISION_SETTINGS,
} from '@/lib/revision-settings-storage';
import { getBudgetConfiguration, saveBudgetConfiguration, fetchBudgetConfigurationAsync } from '@/lib/budget-configuration-storage';
import type { BudgetMode, BudgetConfiguration } from '@/types/budget-configuration.types';

/**
 * Budget type options — kept for backward compatibility with components that
 * haven't yet been migrated to use BudgetMode.
 * @deprecated Prefer using `BudgetMode` from budget-configuration.types.
 */
export type BudgetType = 'none' | 'simple' | 'monthly';

/**
 * Individual budget category permissions.
 * Only meaningful when `budgetMode` is 'category'.
 */
export interface BudgetPermissions {
  costBudgetEnabled: boolean;
  salesBudgetEnabled: boolean;
  resourceBudgetEnabled: boolean;
}

/**
 * Complete budget settings including mode, category permissions, and revision
 * settings.  The `budgetType` field is a legacy shim — prefer `budgetMode`.
 */
export interface BudgetSettings {
  /** @deprecated Use `budgetMode` instead. */
  budgetType: BudgetType;
  /** The active org-level budget mode. */
  budgetMode: BudgetMode;
  permissions: BudgetPermissions;
  revisionSettings: BudgetRevisionSettings;
  /** True when projects with budget data exist — the budget mode is locked. */
  hasBudgetData: boolean;
}

const STORAGE_KEY = 'budget_settings';

const DEFAULT_SETTINGS: BudgetSettings = {
  budgetType: 'monthly',
  budgetMode: 'cost-centre',
  permissions: {
    costBudgetEnabled: true,
    salesBudgetEnabled: true,
    resourceBudgetEnabled: true,
  },
  revisionSettings: DEFAULT_REVISION_SETTINGS,
  hasBudgetData: false,
};

/** Map BudgetConfiguration → BudgetSettings (the shape this hook exposes). */
function configToSettings(
  config: BudgetConfiguration,
  revisionSettings: BudgetRevisionSettings
): BudgetSettings {
  const isEnabled = config.budgetMode !== 'disabled';
  // Legacy budgetType shim
  const budgetType: BudgetType = isEnabled ? 'monthly' : 'none';

  // In Cost Centre mode all three budget tabs are always visible — individual
  // category toggles are irrelevant (PRD §4.4).
  const isCostCentre = config.budgetMode === 'cost-centre';

  return {
    budgetType,
    budgetMode: config.budgetMode,
    permissions: {
      costBudgetEnabled:     isCostCentre ? true : config.costBudgetEnabled,
      salesBudgetEnabled:    isCostCentre ? true : config.salesBudgetEnabled,
      resourceBudgetEnabled: isCostCentre ? true : config.resourceBudgetEnabled,
    },
    revisionSettings,
    hasBudgetData: config.hasBudgetData ?? false,
  };
}

/**
 * Hook to manage budget settings including budget mode, granular permissions
 * and revision settings.
 *
 * Reads the org-level budget configuration from localStorage (the mock
 * persistence layer — see Section 7 of COST_CENTRE_BUDGETING_PRD.md).
 *
 * @example
 * ```tsx
 * const {
 *   settings, isBudgetingEnabled, isCostCentreMode, isCategoryMode,
 *   updateBudgetMode, updatePermissions,
 * } = useBudgetPermissions();
 *
 * if (!isBudgetingEnabled) return null;
 * if (isCostCentreMode) return <CostCentreEditView />;
 * ```
 */
export function useBudgetPermissions() {
  const [settings, setSettings] = useState<BudgetSettings>(() => {
    const config = getBudgetConfiguration();
    return configToSettings(config, getRevisionSettings());
  });

  const fetchedRef = useRef(false);

  // Re-read from localStorage whenever the component mounts, so that changes
  // made in the Settings page are immediately reflected.
  useEffect(() => {
    const config = getBudgetConfiguration();
    setSettings((prev) =>
      configToSettings(config, prev.revisionSettings)
    );
  }, []);

  // Fetch budget configuration from API on mount (localStorage value is initial state)
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    fetchBudgetConfigurationAsync()
      .then((config) => {
        setSettings((prev) => configToSettings(config, prev.revisionSettings));
      })
      .catch(() => { /* fall back to cached/default */ });

    fetchRevisionSettings()
      .then((revSettings) => {
        setSettings((prev) => ({
          ...prev,
          revisionSettings: { ...prev.revisionSettings, ...revSettings },
        }));
      })
      .catch(() => { /* fall back to cached/default */ });

    fetchRevisionReasonCategories()
      .then((categories) => {
        if (categories && categories.length > 0) {
          setSettings((prev) => ({
            ...prev,
            revisionSettings: {
              ...prev.revisionSettings,
              revisionReasonCategories: categories,
            },
          }));
        }
      })
      .catch(() => {
        // Fall back to DEFAULT_REVISION_REASON_CATEGORIES (already set)
      });
  }, []);

  // ── Mutators ──────────────────────────────────────────────────────────────

  /**
   * Update the org-level budget mode and persist to localStorage.
   */
  const updateBudgetMode = useCallback((budgetMode: BudgetMode) => {
    const currentConfig = getBudgetConfiguration();
    const updated: BudgetConfiguration = { ...currentConfig, budgetMode };
    saveBudgetConfiguration(updated);
    setSettings((prev) => ({
      ...prev,
      budgetMode,
      budgetType: budgetMode === 'disabled' ? 'none' : 'monthly',
    }));
  }, []);

  /**
   * @deprecated Use `updateBudgetMode` instead.
   * Kept for backward-compat with components not yet migrated.
   */
  const updateBudgetType = useCallback((budgetType: BudgetType) => {
    const mode: BudgetMode =
      budgetType === 'none' ? 'disabled' : 'category';
    updateBudgetMode(mode);
  }, [updateBudgetMode]);

  /**
   * Update individual budget category toggles and persist.
   * Only meaningful in 'category' mode.
   */
  const updatePermissions = useCallback((permissions: Partial<BudgetPermissions>) => {
    const currentConfig = getBudgetConfiguration();
    const updated: BudgetConfiguration = {
      ...currentConfig,
      ...(permissions.costBudgetEnabled !== undefined && {
        costBudgetEnabled: permissions.costBudgetEnabled,
      }),
      ...(permissions.salesBudgetEnabled !== undefined && {
        salesBudgetEnabled: permissions.salesBudgetEnabled,
      }),
      ...(permissions.resourceBudgetEnabled !== undefined && {
        resourceBudgetEnabled: permissions.resourceBudgetEnabled,
      }),
    };
    saveBudgetConfiguration(updated);
    setSettings((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, ...permissions },
    }));
  }, []);

  /**
   * Update entire settings object at once.
   */
  const updateSettings = useCallback((newSettings: Partial<BudgetSettings>) => {
    setSettings((prev) => ({
      ...prev,
      ...newSettings,
      permissions: {
        ...prev.permissions,
        ...(newSettings.permissions || {}),
      },
    }));
  }, []);

  /**
   * Reset to default settings.
   */
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  /**
   * Update the revision settings for this organisation.
   */
  const updateRevisionSettings = useCallback(
    (revisionSettings: BudgetRevisionSettings) => {
      setSettings((prev) => ({ ...prev, revisionSettings }));
      persistRevisionSettings(revisionSettings).catch((err) => {
        console.error('Failed to persist revision settings:', err);
      });
    },
    []
  );

  /**
   * Return only the enabled revision reason categories, sorted by displayOrder.
   */
  const getEnabledCategories = useCallback((): RevisionReasonCategoryConfig[] => {
    return [...settings.revisionSettings.revisionReasonCategories]
      .filter((cat) => cat.isEnabled)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [settings.revisionSettings.revisionReasonCategories]);

  // ── Derived flags ─────────────────────────────────────────────────────────

  /** True when any budget mode other than 'disabled' is active. */
  const isBudgetingEnabled = settings.budgetMode !== 'disabled';

  /** True when the org is in Cost Centre budgeting mode. */
  const isCostCentreMode = settings.budgetMode === 'cost-centre';

  /** True when the org is in Category budgeting mode. */
  const isCategoryMode = settings.budgetMode === 'category';

  /**
   * True when at least one budget category is enabled.
   * In 'cost-centre' mode all three budget types are always active so this is
   * always true when budgeting is enabled.
   */
  const hasAnyBudgetEnabled =
    isCostCentreMode ||
    (isCategoryMode &&
      (settings.permissions.costBudgetEnabled ||
        settings.permissions.salesBudgetEnabled ||
        settings.permissions.resourceBudgetEnabled));

  return useMemo(
    () => ({
      settings,
      updateBudgetType,
      updateBudgetMode,
      updatePermissions,
      updateSettings,
      resetSettings,
      updateRevisionSettings,
      getEnabledCategories,
      isBudgetingEnabled,
      isCostCentreMode,
      isCategoryMode,
      hasAnyBudgetEnabled,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings]
  );
}
